import React, { useEffect, useState, useRef } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import Header from '../components/Header';
import Footer from '../components/Footer';
import api from '../services/api';
import sessionStore from '../services/sessionStore';
import { motion } from 'framer-motion';

// Styled markdown elements for assistant messages
const markdownComponents = {
  h1: ({ node, children, ...props }) => <h3 className="text-lg font-bold mt-3 mb-1" {...props}>{children}</h3>,
  h2: ({ node, children, ...props }) => <h4 className="text-base font-bold mt-3 mb-1" {...props}>{children}</h4>,
  h3: ({ node, children, ...props }) => <h4 className="text-base font-semibold mt-2 mb-1" {...props}>{children}</h4>,
  p: ({ node, children, ...props }) => <p className="mb-2 last:mb-0" {...props}>{children}</p>,
  ul: ({ node, children, ...props }) => <ul className="list-disc pl-5 mb-2 space-y-1" {...props}>{children}</ul>,
  ol: ({ node, children, ...props }) => <ol className="list-decimal pl-5 mb-2 space-y-1" {...props}>{children}</ol>,
  a: ({ node, children, ...props }) => (
    <a className="text-primary underline break-all" target="_blank" rel="noopener noreferrer" {...props}>{children}</a>
  ),
};

const ChatMessage = ({ message, sender, fontSize = 'text-base' }) => {
  const isUser = sender === 'user';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-5`}
    >
      {!isUser && (
        <div className="h-8 w-8 rounded-full bg-teal-600 flex items-center justify-center text-white font-semibold mr-2 shrink-0 shadow-md">
          AI
        </div>
      )}
      <div
        className={`max-w-[85%] p-3 rounded-lg ${isUser
          ? 'bg-primary text-white rounded-br-none shadow-md'
          : 'bg-white border border-gray-200 text-gray-800 rounded-bl-none shadow-md'}`}
      >
        {isUser ? (
          <div className={`whitespace-pre-wrap ${fontSize}`}>{message}</div>
        ) : (
          <div className={fontSize}>
            <ReactMarkdown components={markdownComponents}>{message}</ReactMarkdown>
          </div>
        )}
      </div>
      {isUser && (
        <div className="h-8 w-8 rounded-full bg-indigo-600 flex items-center justify-center text-white font-semibold ml-2 shrink-0 shadow-md">
          Me
        </div>
      )}
    </motion.div>
  );
};

const ChatStep = ({ step, currentStep }) => {
  const isActive = step === currentStep;
  const isCompleted = step < currentStep;

  return (
    <div className="flex items-center mb-2 relative">
      {step > 1 && (
        <div className={`absolute top-0 left-4 h-full w-0.5 -translate-y-1/2 ${isCompleted ? 'bg-green-500' : 'bg-gray-200'}`}></div>
      )}
      <div
        className={`
          w-9 h-9 rounded-full flex items-center justify-center text-sm font-medium shadow-md
          ${isActive
            ? 'bg-primary text-white ring-2 ring-primary-light ring-offset-2'
            : isCompleted
              ? 'bg-green-500 text-white'
              : 'bg-gray-200 text-gray-500'}
          z-10 transition-all duration-300
        `}
      >
        {isCompleted ? '✓' : step}
      </div>
      <div className={`ml-3 text-sm font-medium ${isActive ? 'text-primary font-semibold' : isCompleted ? 'text-green-600' : 'text-gray-500'}`}>
        {getStepName(step)}
      </div>
    </div>
  );
};

const getStepName = (step) => {
  switch (step) {
    case 1: return 'Confirming Patient Info';
    case 2: return 'Initial Questions';
    case 3: return 'Follow Up Questions';
    case 4: return 'List of Probable Conditions';
    case 5: return 'Researching Medical Journals';
    case 6: return '1st AI Opinion';
    case 7: return '2nd AI Opinion';
    case 8: return 'Patient\'s Questions';
    case 9: return 'Further Questions';
    case 10: return 'Report Generation';
    default: return `Step ${step}`;
  }
};

const PriorVisitChatPage = () => {
  const location = useLocation();
  const chatContainerRef = useRef(null);

  const [formData, setFormData] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [isContinuing, setIsContinuing] = useState(false);
  const [messages, setMessages] = useState([]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [pdfUrl, setPdfUrl] = useState(null); // object URL of the generated PDF blob
  const [pdfFilename, setPdfFilename] = useState(null);
  const [conversationEnded, setConversationEnded] = useState(false);
  const [messageCounter, setMessageCounter] = useState(0);
  const [fontSize, setFontSize] = useState('text-base'); // Default font size
  // Guards the step 4 -> 5 transition so the auto-trigger effect and the
  // send handler can't both fire the research step (double Perplexity call)
  const step5TriggeredRef = useRef(false);

  // The browser owns the conversation; this strips UI-only fields so the
  // history can be resent to the (stateless) backend with each request.
  const toHistory = (msgs) => msgs.map(m => ({ sender: m.sender, message: m.message, step: m.step }));

  // Stream an assistant reply into the chat, growing the last message
  // token by token as the model generates it.
  const streamAssistantReply = async ({ sessionId: sid, message, formData: fd, step, history }) => {
    setMessages(prev => [...prev, { sender: 'assistant', message: '', timestamp: new Date(), step, streaming: true }]);

    try {
      const fullText = await api.streamChatMessage(
        { sessionId: sid, message, formData: fd, step, history },
        (text) => {
          setMessages(prev => {
            const updated = [...prev];
            updated[updated.length - 1] = { ...updated[updated.length - 1], message: text };
            return updated;
          });
        }
      );

      setMessages(prev => {
        const updated = [...prev];
        updated[updated.length - 1] = { ...updated[updated.length - 1], message: fullText, streaming: false };
        return updated;
      });
      return fullText;
    } catch (err) {
      // Remove the placeholder bubble so the error state is clean
      setMessages(prev => (prev.length && prev[prev.length - 1].streaming ? prev.slice(0, -1) : prev));
      throw err;
    }
  };

  // Initialize chat from location state or localStorage
  useEffect(() => {
    const initializeChat = async () => {
      let data = null;
      let id = null;

      // Check if we have form data from navigation state
      if (location.state?.formData) {
        data = location.state.formData;
        id = location.state.sessionId;
        setIsContinuing(location.state.continuing || false);
      } else {
        // Try to get from localStorage if not available in navigation state
        const storedData = localStorage.getItem('formData');
        if (storedData) {
          const parsedData = JSON.parse(storedData);
          data = parsedData;
          id = parsedData.sessionId;
        }
      }

      if (data && id) {
        setFormData(data);
        setSessionId(id);

        try {
          setLoading(true);

          // The browser is the source of truth: restore stored messages if
          // this session was seen before (continue flow or page refresh)
          let storedMessages = location.state?.messages;
          if (!storedMessages || storedMessages.length === 0) {
            const stored = await sessionStore.continueSession(id);
            storedMessages = stored.success ? stored.data.messages : [];
          }

          if (storedMessages && storedMessages.length > 0) {
            setMessages(storedMessages);

            const latestStep = storedMessages.reduce((max, msg) => (msg.step > max ? msg.step : max), 1);
            setCurrentStep(latestStep);
            setMessageCounter(storedMessages.length);
            if (latestStep >= 5) {
              step5TriggeredRef.current = true;
            }
          } else {
            await startConversation(data, id);
          }
        } catch (err) {
          console.error("Failed to initialize chat:", err);
          setError("Failed to connect to the AI service. Please try again later.");
        } finally {
          setLoading(false);
        }
      }
    };

    initializeChat();
  }, [location]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-scroll to bottom of chat
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages]);

  // True while the last message is still growing token by token
  const isStreamingVisible = messages.length > 0
    && messages[messages.length - 1].streaming
    && messages[messages.length - 1].message;

  // Persist the conversation locally so Session History / continue work
  // without any database (skipped mid-stream to avoid a write per token)
  useEffect(() => {
    if (sessionId && messages.length > 0 && !messages[messages.length - 1].streaming) {
      sessionStore.saveSessionSnapshot({
        sessionId,
        formData,
        messages: toHistory(messages),
        status: conversationEnded ? 'completed' : 'active'
      });
    }
  }, [messages, sessionId, formData, conversationEnded]); // eslint-disable-line react-hooks/exhaustive-deps

  // Run the step 5 research call (Perplexity) and append the result.
  // userReply, when provided, is the user's answer to the step 4 conditions
  // list; sending it keeps their reply in the backend chat history.
  const triggerStep5 = async (userReply = null) => {
    if (step5TriggeredRef.current) return;
    step5TriggeredRef.current = true;

    // Add a transition message
    const transitionMessage = {
      sender: 'assistant',
      message: "Now I'll research specific questions that would help differentiate between these potential conditions...",
      timestamp: new Date(),
      step: 4
    };

    setMessages(prev => [...prev, transitionMessage]);
    setLoading(true);

    try {
      await streamAssistantReply({
        sessionId,
        message: userReply || "Generate differential diagnosis research questions",
        formData,
        step: 5, // This will use Perplexity API
        history: toHistory(messages)
      });

      setCurrentStep(5);
      setMessageCounter(prev => prev + 1);
    } catch (err) {
      console.error("Failed to trigger step 5:", err);
      step5TriggeredRef.current = false; // allow a retry
      setError("Failed to get research questions. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Auto-trigger step 5 shortly after the step 4 response is shown
  useEffect(() => {
    if (messages.length === 0 || loading || currentStep !== 4) return;
    const lastMessage = messages[messages.length - 1];
    if (lastMessage.sender !== 'assistant' || lastMessage.step !== 4 || lastMessage.streaming) return;
    if (step5TriggeredRef.current) return;
    // Don't auto-trigger off the transition message itself
    if (lastMessage.message.includes("Now I'll research specific questions")) return;

    // Wait a moment to let the user read the step 4 response
    const timeoutId = setTimeout(() => triggerStep5(), 3000);
    return () => clearTimeout(timeoutId);
  }, [messages, currentStep, loading, sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Check if conversation should end
  useEffect(() => {
    if (currentStep > 9 || messageCounter >= 20) {
      setConversationEnded(true);
    }
  }, [currentStep, messageCounter]);

  // Start initial conversation
  const startConversation = async (data, id) => {
    try {
      await streamAssistantReply({
        sessionId: id,
        message: "Starting consultation",
        formData: data,
        step: 1,
        history: []
      });

      setCurrentStep(1);
      setMessageCounter(prev => prev + 1);
    } catch (err) {
      console.error("Failed to start conversation:", err);
      setError("Failed to connect to the AI service. Please try again later.");
    }
  };

  // Handle sending a message
  const handleSendMessage = async () => {
    if (!currentMessage.trim() || loading || conversationEnded) return;

    // Add user message to chat
    const userMessage = {
      sender: 'user',
      message: currentMessage,
      timestamp: new Date(),
      step: currentStep
    };

    setMessages(prev => [...prev, userMessage]);
    setCurrentMessage('');
    setLoading(true);

    try {
      // Increment message counter
      const newMessageCounter = messageCounter + 1;
      setMessageCounter(newMessageCounter);

      // Determine next step - fixed progression logic
      // This ensures after a user replies, we move to the next appropriate step
      let nextStep = currentStep;

      // If the user responded to the initial assessment, move to step 2
      if (currentStep === 1) {
        nextStep = 2;
      }
      // After information gathering, move to detailed questions
      else if (currentStep === 2) {
        nextStep = 3;
      }
      // After detailed questions, move to potential diagnoses
      else if (currentStep === 3) {
        nextStep = 4;
      }
      // After potential diagnoses response, trigger step 5 (research questions)
      // directly, passing the user's reply so it isn't lost from the chat history
      else if (currentStep === 4) {
        await triggerStep5(currentMessage);
        setLoading(false);
        return; // Exit early as we've already handled the response flow
      }
      // After research questions, move to analysis & treatment
      else if (currentStep === 5) {
        nextStep = 6;
      }
      // After analysis, move to second opinion
      else if (currentStep === 6) {
        nextStep = 7;
      }
      // After second opinion, move to treatment plan
      else if (currentStep === 7) {
        nextStep = 8;
      }
      // After treatment plan, move to final questions
      else if (currentStep === 8) {
        nextStep = 9;
      }

      // Special case for report generation
      if (currentMessage.toLowerCase().includes('generate report') ||
          currentMessage.toLowerCase().includes('create report') ||
          currentMessage.toLowerCase().includes('make report') ||
          currentMessage.toLowerCase().includes('get report')) {
        await handleGeneratePdf();
        return;
      }

      // Stream the reply, sending the browser-owned form data and history
      // so the backend can rebuild the session if it was restarted
      await streamAssistantReply({
        sessionId,
        message: currentMessage,
        formData,
        step: nextStep,
        history: toHistory(messages)
      });

      setCurrentStep(nextStep);
      setMessageCounter(prev => prev + 1);
    } catch (err) {
      console.error("Failed to send message:", err);
      setError("Failed to get a response. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Handle Enter key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Generate PDF
  const handleGeneratePdf = async () => {
    if (!sessionId) {
      setError("Session ID is missing. Please refresh the page and try again.");
      return;
    }

    try {
      setLoading(true);
      setError(null); // Clear any previous errors
      if (pdfUrl) URL.revokeObjectURL(pdfUrl); // Free any previous PDF blob
      setPdfUrl(null);

      // Add a system message about generating PDF
      const systemMessage = {
        sender: 'assistant',
        message: "Generating your comprehensive Patients Empowered Coach report. This may take a moment...",
        timestamp: new Date(),
        step: currentStep
      };

      setMessages(prev => [...prev, systemMessage]);

      // The backend streams the PDF straight back; the report only ever
      // lives in this browser (nothing is stored on the server).
      const { blob, filename } = await api.generatePdfReport(sessionId, formData, toHistory(messages));

      setPdfUrl(URL.createObjectURL(blob));
      setPdfFilename(filename);

      // Add confirmation message
      const confirmationMessage = {
        sender: 'assistant',
        message: "Your PDF report has been generated! You can download it using the button below.",
        timestamp: new Date(),
        step: currentStep
      };

      setMessages(prev => [...prev, confirmationMessage]);
    } catch (err) {
      console.error("Failed to generate PDF:", err);

      // Format a user-friendly error message
      let errorMessage = `Failed to generate PDF report: ${err.message}`;
      if (err.name === 'AbortError' || err.message.includes('timed out')) {
        errorMessage = 'PDF generation timed out. The server might be busy. Please try again later.';
      }

      setError(errorMessage);

      // Add error message to chat
      const chatErrorMessage = {
        sender: 'assistant',
        message: `I'm sorry, I encountered an error while trying to generate your PDF report: ${err.message}. Please try again later.`,
        timestamp: new Date(),
        step: currentStep
      };

      setMessages(prev => [...prev, chatErrorMessage]);
    } finally {
      setLoading(false);
    }
  };

  // Handle PDF download -- the PDF blob is already in the browser's memory
  const handleDownloadPdf = () => {
    if (!pdfUrl) {
      setError('No PDF available. Please generate the PDF first.');
      return;
    }

    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = pdfFilename || `MedicalReport_${new Date().toISOString().split('T')[0]}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Redirect to form if no data is available
  if (!formData && !localStorage.getItem('formData')) {
    return <Navigate to="/priorv-form" />;
  }








  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-teal-400">
      <Header />


      <main className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-8"
        >
          <h1 className="text-3xl font-bold text-gray-900 font-serif">
            <span className="text-primary">
              {isContinuing ? 'Continue Your Doctor Visit Preparation' : 'Prepare for Your Next Doctor Visit'}
            </span> with Patients Empowered Coach
          </h1>

          {isContinuing && (
            <div className="mt-2 text-sm text-gray-600">
              Resuming your previous conversation...
            </div>
          )}
        </motion.div>

        <div className="grid grid-cols-3 md:grid-cols-3 lg:grid-cols-3 gap-4">







          {/* Left sidebar */}
          <div className="grid col-span-1 md:col-span-1 lg:col-span-1">


            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-white rounded-xl shadow-lg p-5 mb-5 border border-gray-200 col-span-1"
            >
              <h3 className="text-lg font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-100 flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-primary" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                </svg>
                Patient Information
              </h3>


              <div className="space-y-0">
                <div className="flex items-center rounded-md p-2 hover:bg-gray-50 transition-colors">
                  <span className="w-20 font-medium text-gray-600">Name:</span>
                  <span className="text-gray-800 font-medium">{formData?.Name}</span>
                </div>
                <div className="flex items-center rounded-md p-2 hover:bg-gray-50 transition-colors">
                  <span className="w-20 font-medium text-gray-600">Age:</span>
                  <span className="text-gray-800 font-medium">{formData?.Age}</span>
                </div>
                <div className="flex items-center rounded-md p-2 hover:bg-gray-50 transition-colors">
                  <span className="w-20 font-smedium text-gray-600">Gender:</span>
                  <span className="text-gray-800 font-medium">{formData?.Gender}</span>
                </div>
              </div>
            </motion.div>



            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-white rounded-xl shadow-lg p-5 border border-gray-200"
            >
              <h3 className="text-lg font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-100 flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-primary" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                </svg>
                Preparation Progress
              </h3>
              <div className="space-y-2 py-0">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(step => (
                  <ChatStep
                    key={step}
                    step={step}
                    currentStep={currentStep}
                  />
                ))}
              </div>
            </motion.div>
          </div>








          {/* Main chat area */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="grid col-span-2 md:col-span-2 lg:col-span-2 bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden h-full col-span-2"
          >
          {/* Chat header */}
          <div className="bg-primary text-white px-6 py-2 flex items-center justify-between shadow-md">
            <div className="flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
              </svg>
              <h2 className="text-lg text-white pt-1 font-semibold">Preparation Chat</h2>
            </div>
            <div className="flex items-center space-x-2">
              <div className="flex items-center bg-white bg-opacity-10 rounded-lg px-2 py-1">
                <button
                  onClick={() => setFontSize('text-sm')}
                  className={`px-2 py-1 rounded-l-md ${fontSize === 'text-sm' ? 'bg-white bg-opacity-30 text-white' : 'text-white text-opacity-70 hover:bg-white hover:bg-opacity-20'}`}
                  title="Small text"
                >
                  A<span className="text-xs">-</span>
                </button>
                <button
                  onClick={() => setFontSize('text-base')}
                  className={`px-2 py-1 ${fontSize === 'text-base' ? 'bg-white bg-opacity-30 text-white' : 'text-white text-opacity-70 hover:bg-white hover:bg-opacity-20'}`}
                  title="Medium text"
                >
                  A
                </button>
                <button
                  onClick={() => setFontSize('text-lg')}
                  className={`px-2 py-1 rounded-r-md ${fontSize === 'text-lg' ? 'bg-white bg-opacity-30 text-white' : 'text-white text-opacity-70 hover:bg-white hover:bg-opacity-20'}`}
                  title="Large text"
                >
                  A<span className="text-xs">+</span>
                </button>
              </div>
              <div className="text-sm bg-white bg-opacity-20 px-3 py-1 rounded-full">
                {isContinuing && messages.length > 0 ? 'Continuing - ' : ''}Step {currentStep}: {getStepName(currentStep)}
              </div>
            </div>
          </div>



            {/* Chat messages */}
            <div
              ref={chatContainerRef}
              className="flex-1 p-6 overflow-y-auto bg-gray-50"
              style={{ maxHeight: 'calc(100vh - 340px)', minHeight: '450px' }}
            >
              {/* Continuation message */}
              {isContinuing && messages.length > 0 && (
                <div className="text-center p-4 mb-6 bg-blue-50 border border-blue-200 rounded-lg shadow-sm max-w-lg mx-auto">
                  <div className="flex items-center justify-center mb-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-600 mr-2" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.293l-3-3a1 1 0 00-1.414 1.414L10.586 9H7a1 1 0 100 2h3.586l-1.293 1.293a1 1 0 101.414 1.414l3-3a1 1 0 000-1.414z" clipRule="evenodd" />
                    </svg>
                    <span className="text-blue-800 font-medium">Welcome back!</span>
                  </div>
                  <p className="text-blue-700 text-sm">
                    Continuing your conversation.
                    You can pick up where you left off or ask new questions.
                  </p>
                </div>
              )}

              {messages.map((msg, index) => (
                <ChatMessage
                  key={index}
                  message={msg.message}
                  sender={msg.sender}
                  fontSize={fontSize}
                />
              ))}

              {loading && !isStreamingVisible && (
                <div className="text-center p-6 my-4 bg-white bg-opacity-70 rounded-lg shadow-sm max-w-md mx-auto">
                  <div className="flex items-center justify-center space-x-2">
                    <div className="inline-block w-3 h-3 bg-primary rounded-full animate-bounce"></div>
                    <div className="inline-block w-3 h-3 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    <div className="inline-block w-3 h-3 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                  </div>
                  <p className="text-gray-600 font-medium mt-3">Patients Empowered Coach is analyzing your information...</p>
                </div>
              )}

              {error && (
                <div className="p-4 bg-red-50 text-red-700 rounded-lg shadow-sm border-l-4 border-red-500 mb-4 max-w-lg mx-auto flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  {error}
                </div>
              )}



              {/* Generate PDF button after step 8 */}
              {currentStep >= 8 && !pdfUrl && !loading && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5 }}
                  className="p-5 bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 rounded-lg shadow-md border border-blue-200 mb-6 text-center max-w-2xl mx-auto"
                >
                  <div className="flex items-center justify-center mb-2 text-primary">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold mb-2 text-blue-800">Preparation Complete!</h3>
                  <p className="mb-4 text-blue-600">You've completed the main preparation. Would you like to generate your comprehensive report now?</p>
                  <button
                    onClick={handleGeneratePdf}
                    className="px-6 py-3 bg-primary hover:bg-primary-dark text-white font-medium rounded-lg transition-colors shadow-md flex items-center justify-center mx-auto space-x-2 transform hover:scale-105 transition-transform duration-200"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V7.414A2 2 0 0015.414 6L12 2.586A2 2 0 0010.586 2H6zm5 6a1 1 0 10-2 0v3.586l-1.293-1.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 11.586V8z" clipRule="evenodd" />
                    </svg>
                    <span>Generate PDF Report</span>
                  </button>
                </motion.div>
              )}



              {/* PDF download button if available */}
              {pdfUrl && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5 }}
                  className="p-5 bg-gradient-to-r from-green-50 to-teal-50 text-green-700 rounded-lg shadow-md border border-green-200 mb-6 text-center max-w-2xl mx-auto"
                >
                  <div className="flex items-center justify-center mb-2 text-green-500">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold mb-2 text-green-800">Success!</h3>
                  <p className="mb-4 text-green-600">Your comprehensive report is ready!</p>
                  <button
                    onClick={handleDownloadPdf}
                    className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors shadow-md flex items-center justify-center mx-auto space-x-2 transform hover:scale-105 transition-transform duration-200"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                    <span>Download PDF Report</span>
                  </button>
                </motion.div>
              )}
            </div>



            {/* Input area */}
            <div className="border-t border-gray-200 p-5 bg-white shadow-inner">
              <div className="flex relative max-w-4xl mx-auto">
                <textarea
                  className="flex-1 p-4 pr-16 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary resize-none shadow-sm"
                  value={currentMessage}
                  onChange={(e) => setCurrentMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={conversationEnded ? "Chat has ended. You can download your report if generated." : "Type your question or symptom here..."}
                  disabled={loading || conversationEnded}
                  rows="2"
                />


                <button
                  className={`absolute right-2 bottom-2 p-3 bg-primary text-white font-medium rounded-lg ${(loading || conversationEnded) ? 'opacity-50 cursor-not-allowed' : 'hover:bg-primary-dark'} shadow-md transition-all duration-200 transform hover:scale-105`}
                  onClick={handleSendMessage}
                  disabled={loading || conversationEnded}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.707l-3-3a1 1 0 00-1.414 1.414L10.586 9H7a1 1 0 100 2h3.586l-1.293 1.293a1 1 0 101.414 1.414l3-3a1 1 0 000-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>


              {!conversationEnded && (
                <div className="text-xs text-gray-400 mt-2 text-center">
                  Press Enter to send your message. You can also click the send button.
                </div>
              )}
            </div>
          </motion.div>

        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PriorVisitChatPage;